class SSoTMessageEmitter extends EventTarget {
  event = 'ssotmessage';

  subscribe(handler) {
    this.addEventListener(this.event, handler);
  }

  unsubscribe(handler) {
    this.removeEventListener(this.event, handler);
  }

  emit(detail) {
    this.dispatchEvent(new CustomEvent(this.event, { detail }));
  }
}

export default SSoTMessageEmitter;

/** Normalized SSOT action constants (title-case). */
export const SSOT_ACTIONS = Object.freeze({
  CREATE: 'Create',
  UPDATE: 'Update',
  REMOVE: 'Remove',
});

/**
 * Parse and gate a raw SSoT event message for a single-model subscription.
 *
 * Returns null if the payload is missing, the model doesn't match, or the data
 * cannot be parsed. Otherwise returns { action, data } where action is normalized
 * to title-case ('Create' | 'Update' | 'Remove') regardless of what SSOT sends.
 *
 * @param {object} eventMessage Raw event from the message emitter.
 * @param {string} expectedModel Model name to accept (e.g. 'Zone', 'Appliance', 'Apikey').
 * @returns {{ action: string, data: object }|null}
 */
export const parseSSoTEvent = (eventMessage, expectedModel) => {
  const payload = eventMessage?.detail?.payload;
  if (!payload || payload.model !== expectedModel) return null;

  const { action: rawAction, data: rawData } = payload;
  // Normalize action to title-case so callers can use === 'Remove' regardless
  // of whether SSOT sends 'remove', 'Remove', or 'REMOVE'.
  const action = rawAction
    ? rawAction.charAt(0).toUpperCase() + rawAction.slice(1).toLowerCase()
    : null;
  if (!action) return null;

  let data;
  try {
    data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
  } catch {
    return null;
  }
  if (!data) return null;

  return { action, data };
};
